/**
 * The chrome shared by `JsonPanel` (one payload) and `JsonDiffPanel` (two).
 *
 * It lives in the PANEL layer, not in the tables, even though it renders
 * inside the table's border. Three reasons, each a concrete failure of the
 * alternative:
 *
 *  - a panel renders its table twice — inline, and again inside the fullscreen
 *    dialog. A fullscreen button owned by the table would appear inside the
 *    dialog it opened, and suppressing it would take a branch inside the table;
 *  - `JsonKeyValueTable` returns early for an empty or scalar value, where
 *    there is no table to hang a toolbar on;
 *  - "copy" on a diff has to answer *which side*, and only the panel knows.
 *
 * So the tables take a dumb `toolbar` slot and render whatever they are given.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Maximize2 } from 'lucide-react';
import { cn } from '@resource-kit/ui';
import { parseIfString } from '@resource-kit/ui/react';

const COPIED_FEEDBACK_MS = 2000;

/** Shared class for the icon buttons in a JSON table's toolbar. */
export const ICON_BUTTON_CLASS =
  'rounded-sm p-1 text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/**
 * Pretty-print a panel's value the same way an operator would paste it.
 * A string payload that is really JSON (e.g. a webhook body carried as
 * `payloadDetail.text`) is parsed first so the copy matches the tree the
 * table shows — copying the raw string would double-encode it into an
 * escaped `"{\"id\":42}"`. Non-JSON strings are emitted verbatim.
 */
export const rawJson = (value: unknown): string => {
  const parsed = parseIfString(value);
  if (typeof parsed === 'string') return parsed;
  return JSON.stringify(parsed, null, 2);
};

/**
 * Copy-to-clipboard icon button with a transient Check confirmation.
 * Shared by the inline toolbar and the fullscreen dialog header so
 * both surfaces behave identically.
 */
export function CopyJsonButton({ value }: { value: unknown }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Ref-held timer so a re-click within the window restarts the countdown
  // instead of letting the first timer flip the icon back early.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      // `navigator.clipboard` is undefined in insecure contexts / some
      // webviews; a throw here is swallowed so the UI never breaks.
      await navigator.clipboard.writeText(rawJson(value));
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      // Best-effort: icon simply stays as Copy when the API is unavailable.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={ICON_BUTTON_CLASS}
      aria-label={t('json.copy')}
      title={t('json.copy')}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {copied && (
        <span role="status" className="sr-only">
          {t('json.copied')}
        </span>
      )}
    </button>
  );
}

/** The button that opens the fullscreen dialog. Never rendered inside one. */
export function ExpandJsonButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={ICON_BUTTON_CLASS}
      aria-label={t('json.expand')}
      title={t('json.expand')}
    >
      <Maximize2 className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * The strip a JSON table renders above its rows, inside its own border.
 *
 * `label` sits left, controls right. Rendered by the panel and handed to the
 * table, so the table never learns what any of these buttons do.
 */
export function JsonTableToolbar({
  label,
  className,
  children,
}: {
  label?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b border-border/60 bg-background/60 px-2 py-1',
        className,
      )}
    >
      {label !== undefined && (
        <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      )}
      <div className="ml-auto flex items-center gap-0.5">{children}</div>
    </div>
  );
}
